import type { FinalizationBehavior } from '@gonfalon/instructions/instructionTypes';
import type { InstructionClause } from '@gonfalon/instructions/instructionTypes/shared';

import type { RuleInstructionKind } from '../rules/constants';

export enum AutomatedReleaseInstructionKind {
  STOP_AUTOMATED_RELEASE = 'stopAutomatedRelease',
  ADVANCE_AUTOMATED_RELEASE = 'advanceAutomatedRelease',
}

export type StartAutomatedReleaseSemanticInstruction = {
  kind: RuleInstructionKind.START_AUTOMATED_RELEASE;
  releaseKind?: 'guarded' | 'progressive';
  ruleId?: string;
  ref?: string;
  clauses?: InstructionClause[];
  description?: string;
  beforeRuleId?: string;
  originalVariationId: string;
  targetVariationId: string;
  randomizationUnit?: string;
  // When `audiences` is set (multi-phase), the top-level `clauses`/`stages` are empty and
  // each phase carries its own clauses and stages on the audience entry instead.
  stages?: Array<{ allocation: number; durationMillis: number }>;
  audiences?: Array<{
    description?: string;
    clauses: InstructionClause[];
    stages: Array<{ allocation: number; durationMillis: number }>;
  }>;
  // Guarded-only fields
  metrics?: Array<{ key: string; isGroup: boolean; metricVersionId?: string; metricGroupId?: string }>;
  metricMonitoringPreferences?: Record<string, { autoRollback: boolean }>;
  // Release-builder cleanup choice. The backend rejects this field on fallthrough
  // releases, so emitters only set it on rule-scoped instructions.
  updateDefaultRuleOnCompletion?: boolean;
  // Only sent when automated phase releases are enabled for the account.
  // Anything missing counts as manual.
  advancementMode?: 'manual' | 'automatic';
  environmentReleasePlanKey?: string;
};

export type StopAutomatedReleaseSemanticInstruction = {
  kind: AutomatedReleaseInstructionKind.STOP_AUTOMATED_RELEASE;
  ruleId?: string;
  finalVariationId?: string;
  releaseId?: string;
  parentReleaseId?: string;
  finalizationBehavior?: FinalizationBehavior;
  fallthrough?: boolean;
};

// Advances a multi-phase release to its next phase. Release-scoped (no rule), so it names
// only the parent release it belongs to.
export type AdvanceAutomatedReleaseSemanticInstruction = {
  kind: AutomatedReleaseInstructionKind.ADVANCE_AUTOMATED_RELEASE;
  parentReleaseId: string;
};

export type AutomatedReleaseSemanticInstruction =
  | StartAutomatedReleaseSemanticInstruction
  | StopAutomatedReleaseSemanticInstruction
  | AdvanceAutomatedReleaseSemanticInstruction;
